import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import MainLayout from '../../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Receipt,
  Search,
  Filter,
  Download,
  Eye,
  Clock,
  CheckCircle2,
  AlertTriangle,
  Euro,
  Calendar,
  FileText,
  Loader2,
  CreditCard,
  Building2,
  Wallet,
  MoreVertical,
  FolderKanban,
  Briefcase,
  Gift,
  Upload,
  X
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import api from '../../lib/axios';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

const ClientInvoicesPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [billingData, setBillingData] = useState({ items: [], stats: {}, gift_history: [] });
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [selectedItem, setSelectedItem] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showGiftHistory, setShowGiftHistory] = useState(false);
  const [paying, setPaying] = useState({});
  const [uploading, setUploading] = useState(null);

  useEffect(() => {
    fetchBillingData();
  }, []);

  const fetchBillingData = async () => {
    setLoading(true);
    try {
      const response = await api.get('/client/billing-summary');
      setBillingData(response.data || { items: [], stats: {}, gift_history: [] });
    } catch (error) {
      console.error('Error fetching billing data:', error);
      toast.error('Erreur lors du chargement des données de facturation');
    } finally {
      setLoading(false);
    }
  };

  const handlePay = async (item, method) => {
    if (item.type !== 'service') {
      toast.info('Le paiement en ligne est réservé aux services à la demande');
      return;
    }
    
    setPaying({ ...paying, [item.id]: true });
    try {
      const response = await api.post(`/service-invoices/${item.id}/pay`, {
        payment_method: method
      });
      
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        toast.success('Paiement enregistré !');
        fetchBillingData();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du paiement');
    } finally {
      setPaying({ ...paying, [item.id]: false });
    }
  };

  const handleUploadInvoice = async (item, file) => {
    if (item.type !== 'project') return;
    
    setUploading(item.id);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('receipt_id', item.id);
    
    try {
      await api.post('/client/receipts/upload-invoice', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Facture téléchargée avec succès');
      fetchBillingData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du téléchargement');
    } finally {
      setUploading(null);
    }
  };

  const openDetail = (item) => {
    setSelectedItem(item);
    setShowDetailDialog(true);
  };

  const filteredItems = billingData.items.filter(item => {
    const matchesSearch = 
      item.reference?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.label?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.project_name?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = filterStatus === 'all' || item.status === filterStatus;
    const matchesType = filterType === 'all' || item.type === filterType;
    
    return matchesSearch && matchesStatus && matchesType;
  });

  const getStatusBadge = (status) => {
    const styles = {
      pending: { class: 'bg-yellow-100 text-yellow-700 border-yellow-200', icon: Clock, label: 'En attente' },
      paid: { class: 'bg-green-100 text-green-700 border-green-200', icon: CheckCircle2, label: 'Payé' },
      overdue: { class: 'bg-red-100 text-red-700 border-red-200', icon: AlertTriangle, label: 'En retard' },
      pending_invoice: { class: 'bg-orange-100 text-orange-700 border-orange-200', icon: Upload, label: 'Facture à joindre' },
      invoice_attached: { class: 'bg-green-100 text-green-700 border-green-200', icon: CheckCircle2, label: 'Complet' },
      cancelled: { class: 'bg-gray-100 text-gray-600 border-gray-200', icon: X, label: 'Annulé' }
    };
    const style = styles[status] || styles.pending;
    return (
      <Badge variant="outline" className={cn('font-medium flex items-center gap-1', style.class)}>
        {style.icon && <style.icon className="w-3 h-3" />}
        {style.label}
      </Badge>
    );
  };

  const getTypeBadge = (type) => {
    if (type === 'service') {
      return (
        <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
          <Briefcase className="w-3 h-3 mr-1" />
          Service
        </Badge>
      );
    }
    return (
      <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
        <FolderKanban className="w-3 h-3 mr-1" />
        Projet
      </Badge>
    );
  };

  const stats = billingData.stats;

  return (
    <MainLayout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-[#1a365d] flex items-center gap-3" data-testid="billing-title">
              <Receipt className="w-8 h-8 text-blue-600" />
              Mon Espace Facturation
            </h1>
            <p className="text-[#64748b] mt-1">
              {filteredItems.length} élément(s) • 
              {stats.pending_amount > 0 && (
                <span className="text-orange-600 font-medium ml-1">
                  {stats.pending_amount?.toLocaleString('fr-FR')}€ en attente
                </span>
              )}
            </p>
          </div>
          
          {/* Gift codes button */}
          {billingData.gift_history?.length > 0 && (
            <Button
              variant="outline"
              onClick={() => setShowGiftHistory(true)}
              className="flex items-center gap-2"
            >
              <Gift className="w-4 h-4 text-pink-500" />
              Mes codes cadeaux ({stats.gift_hours_total}h)
            </Button>
          )}
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <Card className="card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <FileText className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Total</p>
                  <p className="text-2xl font-bold">{stats.total_items || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-indigo-100 rounded-lg">
                  <Briefcase className="h-5 w-5 text-indigo-600" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Services</p>
                  <p className="text-2xl font-bold">{stats.services_count || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <FolderKanban className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Projets</p>
                  <p className="text-2xl font-bold">{stats.projects_count || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Euro className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Payé</p>
                  <p className="text-lg font-bold text-green-600">
                    {(stats.paid_amount || 0).toLocaleString('fr-FR')}€
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <Clock className="h-5 w-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">En attente</p>
                  <p className="text-lg font-bold text-orange-600">
                    {(stats.pending_amount || 0).toLocaleString('fr-FR')}€
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="card mb-6">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Rechercher par référence, service ou projet..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                  data-testid="billing-search"
                />
              </div>
              
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="w-full md:w-48" data-testid="filter-type">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les types</SelectItem>
                  <SelectItem value="service">Services à la demande</SelectItem>
                  <SelectItem value="project">Facturation projet</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-full md:w-48" data-testid="filter-status">
                  <SelectValue placeholder="Statut" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les statuts</SelectItem>
                  <SelectItem value="pending">En attente</SelectItem>
                  <SelectItem value="paid">Payé</SelectItem>
                  <SelectItem value="overdue">En retard</SelectItem>
                  <SelectItem value="pending_invoice">Facture à joindre</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Billing Table */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : filteredItems.length === 0 ? (
          <Card className="card">
            <CardContent className="py-12 text-center">
              <Receipt className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-semibold mb-2">Aucun élément</h3>
              <p className="text-muted-foreground">
                {searchTerm || filterType !== 'all' || filterStatus !== 'all' 
                  ? 'Aucun élément ne correspond à vos critères' 
                  : 'Vous n\'avez pas encore de factures ou règlements'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <Card className="card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="billing-table">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="text-left p-4 font-medium text-slate-600">Type</th>
                    <th className="text-left p-4 font-medium text-slate-600">Référence</th>
                    <th className="text-left p-4 font-medium text-slate-600">Description</th>
                    <th className="text-left p-4 font-medium text-slate-600">Date</th>
                    <th className="text-right p-4 font-medium text-slate-600">Montant HT</th>
                    <th className="text-right p-4 font-medium text-slate-600">Total TTC</th>
                    <th className="text-center p-4 font-medium text-slate-600">Statut</th>
                    <th className="text-right p-4 font-medium text-slate-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredItems.map((item) => (
                    <tr key={item.id} className="border-b hover:bg-slate-50 transition-colors">
                      <td className="p-4">
                        {getTypeBadge(item.type)}
                      </td>
                      <td className="p-4">
                        <span className="font-mono font-medium text-[#1a365d]">
                          {item.reference || '-'}
                        </span>
                        {item.is_recurring && (
                          <Badge variant="outline" className="ml-2 text-xs">Récurrent</Badge>
                        )}
                      </td>
                      <td className="p-4">
                        <p className="font-medium">{item.label}</p>
                        {item.project_name && item.type === 'service' && (
                          <p className="text-xs text-slate-500 flex items-center gap-1">
                            <FolderKanban className="w-3 h-3" />
                            {item.project_name}
                          </p>
                        )}
                        {item.service_type && item.type === 'service' && (
                          <p className="text-xs text-slate-500">{item.service_type}</p>
                        )}
                      </td>
                      <td className="p-4">
                        <div className="text-sm">
                          {item.created_at ? (
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4 text-slate-400" />
                              {new Date(item.created_at).toLocaleDateString('fr-FR')}
                            </div>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                          {item.due_date && item.status !== 'paid' && (
                            <p className="text-xs text-orange-600 mt-1">
                              Échéance: {new Date(item.due_date).toLocaleDateString('fr-FR')}
                            </p>
                          )}
                        </div>
                      </td>
                      <td className="p-4 text-right font-mono">
                        {item.amount_ht?.toLocaleString('fr-FR')}€
                      </td>
                      <td className="p-4 text-right font-mono font-bold">
                        {item.total_ttc?.toLocaleString('fr-FR')}€
                      </td>
                      <td className="p-4 text-center">
                        {getStatusBadge(item.status)}
                        {item.paid_at && (
                          <p className="text-xs text-slate-500 mt-1">
                            {new Date(item.paid_at).toLocaleDateString('fr-FR')}
                          </p>
                        )}
                      </td>
                      <td className="p-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {/* Pay button for services */}
                          {item.type === 'service' && (item.status === 'pending' || item.status === 'overdue') && (
                            <Button
                              size="sm"
                              onClick={() => handlePay(item, 'stripe')}
                              disabled={paying[item.id]}
                              className="bg-blue-600 hover:bg-blue-700"
                              data-testid={`pay-btn-${item.id}`}
                            >
                              {paying[item.id] ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <>
                                  <CreditCard className="w-4 h-4 mr-1" />
                                  Payer
                                </>
                              )}
                            </Button>
                          )}
                          
                          {/* Upload invoice button for project receipts */}
                          {item.type === 'project' && item.status === 'pending_invoice' && (
                            <label className="cursor-pointer">
                              <input
                                type="file"
                                accept=".pdf"
                                className="hidden"
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (file) handleUploadInvoice(item, file);
                                }}
                                disabled={uploading === item.id}
                              />
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-orange-300 text-orange-600 hover:bg-orange-50"
                                disabled={uploading === item.id}
                                asChild
                              >
                                <span>
                                  {uploading === item.id ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <>
                                      <Upload className="w-4 h-4 mr-1" />
                                      Joindre facture
                                    </>
                                  )}
                                </span>
                              </Button>
                            </label>
                          )}
                          
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon">
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => openDetail(item)}>
                                <Eye className="w-4 h-4 mr-2" />
                                Voir détails
                              </DropdownMenuItem>
                              {item.invoice_pdf_url && (
                                <DropdownMenuItem asChild>
                                  <a href={`${process.env.REACT_APP_BACKEND_URL}${item.invoice_pdf_url}`} target="_blank" rel="noopener noreferrer">
                                    <Download className="w-4 h-4 mr-2" />
                                    Télécharger facture
                                  </a>
                                </DropdownMenuItem>
                              )}
                              {item.receipt_url && (
                                <DropdownMenuItem asChild>
                                  <a href={`${process.env.REACT_APP_BACKEND_URL}${item.receipt_url}`} target="_blank" rel="noopener noreferrer">
                                    <FileText className="w-4 h-4 mr-2" />
                                    Voir reçu
                                  </a>
                                </DropdownMenuItem>
                              )}
                              {item.type === 'service' && (item.status === 'pending' || item.status === 'overdue') && item.allow_wallet_payment && (
                                <DropdownMenuItem onClick={() => handlePay(item, 'wallet')}>
                                  <Wallet className="w-4 h-4 mr-2" />
                                  Payer avec Wallet
                                </DropdownMenuItem>
                              )}
                              {item.type === 'service' && (item.status === 'pending' || item.status === 'overdue') && item.allow_bank_transfer && (
                                <DropdownMenuItem onClick={() => handlePay(item, 'bank_transfer')}>
                                  <Building2 className="w-4 h-4 mr-2" />
                                  Virement bancaire
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        {/* Detail Dialog */}
        <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Receipt className="w-5 h-5 text-blue-600" />
                Détails
              </DialogTitle>
            </DialogHeader>
            
            {selectedItem && (
              <div className="space-y-4 py-4">
                {/* Header */}
                <div className="flex justify-between items-start p-4 bg-slate-50 rounded-xl">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      {getTypeBadge(selectedItem.type)}
                    </div>
                    <p className="font-mono text-lg font-bold text-[#1a365d]">{selectedItem.reference}</p>
                    <p className="text-sm text-slate-500">
                      {selectedItem.created_at && `Créé le ${new Date(selectedItem.created_at).toLocaleDateString('fr-FR')}`}
                    </p>
                  </div>
                  {getStatusBadge(selectedItem.status)}
                </div>
                
                {/* Description */}
                <div className="p-4 border rounded-xl">
                  <p className="text-sm text-slate-500 mb-1">
                    {selectedItem.type === 'service' ? 'Service' : 'Projet'}
                  </p>
                  <p className="font-medium">{selectedItem.label}</p>
                  {selectedItem.description && (
                    <p className="text-sm text-slate-600 mt-1">{selectedItem.description}</p>
                  )}
                  {selectedItem.project_name && selectedItem.type === 'service' && (
                    <Badge variant="outline" className="mt-2">
                      <FolderKanban className="w-3 h-3 mr-1" />
                      {selectedItem.project_name}
                    </Badge>
                  )}
                </div>
                
                {/* Amounts */}
                <div className="space-y-2">
                  <div className="flex justify-between p-3 bg-slate-50 rounded-lg">
                    <span className="text-slate-600">Montant HT</span>
                    <span className="font-mono">{selectedItem.amount_ht?.toLocaleString('fr-FR')}€</span>
                  </div>
                  <div className="flex justify-between p-3 bg-slate-50 rounded-lg">
                    <span className="text-slate-600">TVA ({selectedItem.tax_rate}%)</span>
                    <span className="font-mono">{selectedItem.tax_amount?.toLocaleString('fr-FR')}€</span>
                  </div>
                  <div className="flex justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <span className="font-medium text-blue-800">Total TTC</span>
                    <span className="font-mono font-bold text-blue-800">{selectedItem.total_ttc?.toLocaleString('fr-FR')}€</span>
                  </div>
                </div>
                
                {/* Due date */}
                {selectedItem.due_date && (
                  <div className="flex items-center gap-3 p-3 rounded-lg border">
                    <Calendar className="w-5 h-5 text-slate-400" />
                    <div>
                      <p className="text-sm text-slate-500">Date d&apos;échéance</p>
                      <p className="font-medium">{new Date(selectedItem.due_date).toLocaleDateString('fr-FR')}</p>
                    </div>
                  </div>
                )}
                
                {/* Payment info */}
                {(selectedItem.status === 'paid' || selectedItem.status === 'invoice_attached') && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-xl">
                    <p className="text-sm text-green-700 mb-2">
                      {selectedItem.type === 'project' ? 'Paiement reçu' : 'Payé'}
                    </p>
                    <div className="flex justify-between">
                      <span className="text-green-600">Date</span>
                      <span className="font-medium text-green-800">
                        {selectedItem.paid_at ? new Date(selectedItem.paid_at).toLocaleDateString('fr-FR') : '-'}
                      </span>
                    </div>
                    {selectedItem.paid_via && (
                      <div className="flex justify-between mt-1">
                        <span className="text-green-600">Moyen de paiement</span>
                        <span className="text-green-800">{selectedItem.paid_via}</span>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Actions */}
                <div className="flex gap-3 pt-4">
                  {selectedItem.invoice_pdf_url && (
                    <Button variant="outline" className="flex-1" asChild>
                      <a href={`${process.env.REACT_APP_BACKEND_URL}${selectedItem.invoice_pdf_url}`} target="_blank" rel="noopener noreferrer">
                        <Download className="w-4 h-4 mr-2" />
                        Télécharger facture
                      </a>
                    </Button>
                  )}
                  {selectedItem.type === 'service' && (selectedItem.status === 'pending' || selectedItem.status === 'overdue') && (
                    <Button 
                      className="flex-1 bg-blue-600 hover:bg-blue-700"
                      onClick={() => {
                        setShowDetailDialog(false);
                        handlePay(selectedItem, 'stripe');
                      }}
                    >
                      <CreditCard className="w-4 h-4 mr-2" />
                      Payer maintenant
                    </Button>
                  )}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Gift History Dialog */}
        <Dialog open={showGiftHistory} onOpenChange={setShowGiftHistory}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Gift className="w-5 h-5 text-pink-500" />
                Mes codes cadeaux utilisés
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-3 py-4">
              {billingData.gift_history?.length === 0 ? (
                <p className="text-center text-slate-500 py-4">Aucun code cadeau utilisé</p>
              ) : (
                <>
                  <div className="p-3 bg-pink-50 rounded-lg border border-pink-200 mb-4">
                    <p className="text-sm text-pink-700">
                      Total crédité : <span className="font-bold">{stats.gift_hours_total}h</span> de prestations
                    </p>
                  </div>
                  {billingData.gift_history?.map((gift, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 border rounded-lg">
                      <div>
                        <p className="font-mono font-medium">{gift.code}</p>
                        <p className="text-xs text-slate-500">
                          {gift.redeemed_at && new Date(gift.redeemed_at).toLocaleDateString('fr-FR')}
                        </p>
                      </div>
                      <Badge className="bg-pink-100 text-pink-700">
                        +{gift.value_hours}h
                      </Badge>
                    </div>
                  ))}
                </>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </MainLayout>
  );
};

export default ClientInvoicesPage;
